// src/features/checklist/useChecklist.js
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { isLineChecklistEligible } from "./utils";

/**
 * Samler all sjekkliste-logikk i ett sted.
 * Avhengigheter injiseres for å unngå import-sti-problemer og lette testing.
 */
export default function useChecklist({
  svcId,
  serviceLines,
  apiBaseUrl,
  getChecklistsForServiceSlugs, // function (slugs) => Promise<items[]>
}) {
  // Modal/lasting
  const [open, setOpen] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  // Data
  const [items, setItems] = useState([]);              // [{punktSlug, tittel, visKommentar, visOk, visUtfort, sortering}]
  const [values, setValues] = useState({});            // { slug: { okValue, doneValue, commentValue, avvikValue } }
  const [visibility, setVisibility] = useState({});    // { slug: true|false }
  const [visBusySlug, setVisBusySlug] = useState(null);

   // Har vi allerede lastet sjekklisten én gang?
  const loadedRef = useRef(false);

  // UI
  const [showHidden, setShowHidden] = useState(false);

  // Avledet
  const eligible = useMemo(
    () => (serviceLines || []).some(isLineChecklistEligible),
    [serviceLines]
  );

  const slugs = useMemo(
    () => (serviceLines || []).map((l) => (l?.slug || "").trim()).filter(Boolean),
    [serviceLines]
  );

 const unprocessedCount = useMemo(() => {
  if (!Array.isArray(items) || !items.length) return 0;
  return items.reduce((count, it) => {
    const isVisible = visibility[it.punktSlug] !== false; // default true
    if (!isVisible) return count; // skjult = behandlet
    const v = values[it.punktSlug] || {};
    const hasAny =
      !!v.okValue ||
      !!v.doneValue ||
      !!v.avvikValue ||                    // 👈 NYTT
      !!(v.commentValue || "").trim();
    return hasAny ? count : count + 1;
  }, 0);
}, [items, values, visibility]);


const summary = useMemo(() => {
  if (!Array.isArray(items) || !items.length) return [];

  return items
    .map((it) => {
      const v = values[it.punktSlug] || {};

      // 👇 Punktet må være synlig både i backend (it.visible) og lokalt (visibility-map)
      const isVisible =
        (typeof it.visible === "boolean" ? it.visible : true) &&
        visibility[it.punktSlug] !== false;

      if (!isVisible) return null;

      const hasAny =
        !!v.okValue ||
        !!v.doneValue ||
        !!v.avvikValue ||
        (v.commentValue || "").trim().length > 0;

      if (!hasAny) return null;

      return {
        slug: it.punktSlug,
        title: it.tittel,
        ok: !!v.okValue && !!it.visOk,
        done: !!v.doneValue && !!it.visUtfort,
        deviation: !!v.avvikValue,
        canDeviation: !!it.visUtfort, // 👈 forteller om "Avvik" i det hele tatt er mulig
        comment: it.visKommentar ? (v.commentValue || "").trim() : "",
      };
    })
    .filter(Boolean);
}, [items, values, visibility]);



  const hasChecklist = useMemo(
    () => eligible && items.length > 0,
    [eligible, items]
  );

  // Helpers
  const setValue = useCallback((slug, patch) => {
    setValues((prev) => ({
      ...prev,
      [slug]: { ...(prev[slug] || {}), ...patch },
    }));
  }, []);

const buildPayload = useCallback(
  (overrideVisibility = null) =>
    items.map((it) => {
      const v = values[it.punktSlug] || {};
      const isVisible =
        overrideVisibility && overrideVisibility.slug === it.punktSlug
          ? overrideVisibility.value
          : visibility[it.punktSlug] !== false; // default true
      return {
        punktSlug: it.punktSlug,
        title: it.tittel,
        okShown: !!it.visOk,
        doneShown: !!it.visUtfort,
        commentShown: !!it.visKommentar,
        okValue: !!v.okValue,
        doneValue: !!v.doneValue,
        commentValue: (v.commentValue || "").trim(),
        avvikValue: !!v.avvikValue,              // 👈 VIKTIG LINJE
        visible: isVisible,
      };
    }),
  [items, values, visibility]
);


  // IO
  const loadSaved = useCallback(async () => {
    if (!svcId) return;
    setLoadingSaved(true);
    try {
      const r = await fetch(
        `${apiBaseUrl}/workshop-services/${svcId}/checklist?includeHidden=1`
      );
      if (!r.ok) {
        setValues({});
        setVisibility({});
        return;
      }
      const arr = await r.json();
      const vals = {};
      const vis = {};
      (arr || []).forEach((row) => {
        vals[row.punktSlug] = {
          okValue: !!row.okValue,
          doneValue: !!row.doneValue,
          commentValue: row.commentValue || "",
          avvikValue: !!row.avvikValue,          // 👈 LESER FRA SERVER
        };
        vis[row.punktSlug] = row.visible !== false;
      });
      setValues(vals);
      setVisibility(vis);
    } finally {
      setLoadingSaved(false);
    }
  }, [svcId, apiBaseUrl]);

  const fetchItems = useCallback(async () => {
    if (!slugs.length) {
      setItems([]);
      setValues({});
      setVisibility({});
      return;
    }
    setLoadingItems(true);
    try {
      const raw = await getChecklistsForServiceSlugs(slugs);
      // dedup + sort
      const dedup = Object.values(
        (raw || []).reduce((acc, it) => {
          if (!it?.punktSlug) return acc;
          if (!acc[it.punktSlug]) acc[it.punktSlug] = it;
          return acc;
        }, {})
      ).sort((a, b) => {
        const sa = a.sortering ?? 0;
        const sb = b.sortering ?? 0;
        if (sa !== sb) return sa - sb;
        return (a.tittel || "").localeCompare(b.tittel || "", "no");
      });

      setItems(dedup);
      await loadSaved();
      loadedRef.current = true;
    } finally {
      setLoadingItems(false);
    }
  }, [slugs, getChecklistsForServiceSlugs, loadSaved]);

  const openModal = useCallback(async () => {
    setOpen(true);
    await fetchItems();
  }, [fetchItems]);

  const closeModal = useCallback(() => setOpen(false), []);

  const save = useCallback(async () => {
    if (!svcId) return;
    const payload = buildPayload();
    const r = await fetch(`${apiBaseUrl}/workshop-services/${svcId}/checklist`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error("Kunne ikke lagre sjekkliste");
    setOpen(false);
  }, [svcId, apiBaseUrl, buildPayload]);

  const setItemVisibility = useCallback(
    async (slug, isVisible) => {
      if (!svcId || !slug) return;
      setVisBusySlug(slug);
      setVisibility((prev) => ({ ...prev, [slug]: !!isVisible }));
      try {
        const payload = buildPayload({ slug, value: !!isVisible });
        const r = await fetch(`${apiBaseUrl}/workshop-services/${svcId}/checklist`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error("Kunne ikke oppdatere synlighet");
      } catch (e) {
        // rollback
        setVisibility((prev) => ({ ...prev, [slug]: !isVisible }));
        throw e;
      } finally {
        setVisBusySlug(null);
      }
    },
    [svcId, apiBaseUrl, buildPayload]
  );

  // Hold state ryddig hvis ikke kvalifisert
  useEffect(() => {
    if (!eligible) {
      setItems([]);
      setValues({});
      setVisibility({});
    }
  }, [eligible]);

    // Auto-last sjekkliste når service er kvalifisert og vi har slugs
  useEffect(() => {
    if (!eligible) return;           // ingen sjekkliste for denne servicen
    if (!svcId) return;              // trenger service-id for å lagre/lese
    if (!slugs.length) return;       // ingen service-slugs -> ingenting å hente
    if (loadingItems) return;        // pågår allerede
    if (loadedRef.current) return;   // allerede lastet én gang

    fetchItems();
  }, [eligible, svcId, slugs, loadingItems, fetchItems]);


  return {
    // state
    open,
    loadingItems,
    loadingSaved,
    items,
    values,
    visibility,
    showHidden,
    visBusySlug,

    // derived
    eligible,
    hasChecklist,
    unprocessedCount,
    summary,

    // actions
    setValue,
    setShowHidden,
    setItemVisibility,
    openModal,
    closeModal,
    save,
  };
}
